import type {
  EventBucketData,
  EventsFetchResponse,
  ProviderFetchResponse,
  ProviderFetchResponseSuccess,
  VehicleData
} from "@dealer-center/ddc-pages-inventory-archiver-interface";
import { useQuery } from "@tanstack/react-query";

const queryKeyBase = "ddc-pages-inventory-archiver-events";
const fetchEventsForAccount = async (
  bucket: string,
  accountId: string,
  providers: string[]
): Promise<VehicleData[]> => {
  const eventData: VehicleData[] = [];
  const params = new URLSearchParams();
  for (const provider of providers) {
    let hasNext = true;
    while (hasNext) {
      const paramsString = `${params.toString() ? `?${params}` : ""}`;
      const res = await fetch(
        `/invsup/nxlambda/inventoryArchiver/getEventData/${bucket}/${accountId}/${provider}${paramsString}`
      );
      if (res.ok) {
        const data: EventsFetchResponse = await res.json();
        if (data.isTruncated && data.nextContinuationToken) {
          params.append("nextContinuationToken", data.nextContinuationToken);
          params.append("isTruncated", data.isTruncated.toString());
          hasNext = true;
        } else {
          hasNext = false;
        }
        eventData.push(...data.vehicleData);
      } else {
        throw new Error(res.statusText);
      }
    }
  }
  return eventData;
};

export function useGetAccountEvents(
  accountName: string,
  providers: ProviderFetchResponse
) {
  return useQuery({
    queryKey: [queryKeyBase, { accountName, providers }],
    queryFn: async (): Promise<EventBucketData> => {
      return {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        preData: await fetchEventsForAccount(
          "pre",
          accountName?.trim() || "",
          (providers as ProviderFetchResponseSuccess) || []
        ),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        postData: await fetchEventsForAccount(
          "post",
          accountName?.trim() || "",
          (providers as ProviderFetchResponseSuccess) || []
        )
      };
    },
    // wait for a list of providers to run query, dependant on fetchProviders
    enabled:
      !!providers &&
      !!accountName &&
      accountName.trim().length > 0 &&
      Array.isArray(providers) &&
      providers.length > 0
  });
}
